import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  authConfig, authorizeEndpoint, logoutEndpoint, tokenEndpoint,
} from '../config/auth';
import {
  codeChallengeS256, generateCodeVerifier, randomState,
} from '../utils/pkce';

// Public-client Cognito PKCE. All state (verifier, return path) lives in
// sessionStorage — cleared on tab close, isolated per tab.
const SS_VERIFIER    = 'ps.pkce.verifier';
const SS_STATE       = 'ps.pkce.state';
const SS_RETURN_PATH = 'ps.auth.returnPath';

// Module-scoped so it survives React 18 StrictMode's mount → unmount → mount
// cycle in dev. Cognito auth codes are single-use — the second exchange
// attempt gets `invalid_grant`. Guarding on the code value here dedupes.
const consumedAuthCodes = new Set<string>();
// Tokens: id_token drives auth; refresh_token allows silent renewal for 30d.
// localStorage so a full page refresh keeps you signed in.
const LS_ID_TOKEN    = 'ps.token.id';
const LS_REFRESH     = 'ps.token.refresh';
const LS_ACCESS      = 'ps.token.access';

type Claims = {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  exp?: number;
  'cognito:groups'?: string[];
  [k: string]: unknown;
};

// APPROVED_GROUPS mirrors the server-side APPROVED_GROUPS env var. Kept in
// sync manually — the backend is still the authoritative gate (frontend
// awareness just avoids one unnecessary redirect for users we already know
// don't have access).
const APPROVED_GROUPS = ['beta', 'admin'];

interface AuthCtx {
  ready: boolean;                        // finished initial hydration + any callback processing
  isAuthenticated: boolean;
  isApproved: boolean;                   // caller's cognito:groups intersects APPROVED_GROUPS
  user: Claims | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  /** Bearer to attach to API requests. Refreshes silently when close to expiry. */
  getIdToken: () => Promise<string | null>;
  /** Force a token refresh — used after invite redemption so the new
   *  cognito:groups claim lands immediately. Resolves to the new token
   *  (or null if refresh failed). */
  forceRefresh: () => Promise<string | null>;
  displayName: () => string;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export const useAuth = (): AuthCtx => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used inside <AuthProvider>');
  return c;
};

const decodeJwt = (jwt: string): Claims => {
  const [, payload] = jwt.split('.');
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
};

const isExpired = (jwt: string, skewSec = 30): boolean => {
  try {
    const { exp } = decodeJwt(jwt);
    if (!exp) return true;
    return exp - skewSec < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
};

/** POST application/x-www-form-urlencoded to Cognito /oauth2/token. */
const postToken = async (params: Record<string, string>): Promise<{
  id_token: string; access_token: string; refresh_token?: string; expires_in: number;
}> => {
  const body = new URLSearchParams(params).toString();
  const resp = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!resp.ok) {
    const t = await resp.text();
    throw new Error(`token endpoint ${resp.status}: ${t}`);
  }
  return resp.json();
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [ready, setReady]           = useState(false);
  const [user, setUser]             = useState<Claims | null>(null);
  const [idTokenState, setIdTokenS] = useState<string | null>(null);

  const applyTokens = useCallback((toks: {
    id_token: string; access_token?: string; refresh_token?: string;
  }) => {
    localStorage.setItem(LS_ID_TOKEN, toks.id_token);
    if (toks.access_token)  localStorage.setItem(LS_ACCESS, toks.access_token);
    if (toks.refresh_token) localStorage.setItem(LS_REFRESH, toks.refresh_token);
    setIdTokenS(toks.id_token);
    setUser(decodeJwt(toks.id_token));
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(LS_ID_TOKEN);
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
    setIdTokenS(null);
    setUser(null);
  }, []);

  const signIn = useCallback(async () => {
    if (!authConfig.domain || !authConfig.clientId) {
      // eslint-disable-next-line no-alert
      alert('Auth is not configured — set REACT_APP_COGNITO_DOMAIN and REACT_APP_COGNITO_CLIENT_ID.');
      return;
    }
    const verifier  = generateCodeVerifier();
    const challenge = await codeChallengeS256(verifier);
    const state     = randomState();
    sessionStorage.setItem(SS_VERIFIER, verifier);
    sessionStorage.setItem(SS_STATE, state);
    sessionStorage.setItem(SS_RETURN_PATH, window.location.pathname + window.location.search);

    const qs = new URLSearchParams({
      client_id: authConfig.clientId,
      response_type: 'code',
      scope: authConfig.scopes.join(' '),
      redirect_uri: authConfig.redirectUri(),
      state,
      code_challenge_method: 'S256',
      code_challenge: challenge,
      // Skip the Cognito "chooser" step — go straight to Google.
      identity_provider: 'Google',
      // Force Google's account picker every time. Without this, Cognito's
      // /oauth2/authorize?identity_provider=Google silently re-uses Google's
      // still-valid session cookie — even after a full Cognito sign-out —
      // so users get instantly re-authenticated as the same identity.
      // `select_account` gives the picker without forcing password re-entry.
      prompt: 'select_account',
    }).toString();
    window.location.assign(`${authorizeEndpoint()}?${qs}`);
  }, []);

  const signOut = useCallback(() => {
    clear();
    // Hosted-UI logout clears the Cognito SSO cookie so switching Google
    // accounts works. It then redirects back to logoutUri.
    const qs = new URLSearchParams({
      client_id: authConfig.clientId,
      logout_uri: authConfig.logoutUri(),
    }).toString();
    window.location.assign(`${logoutEndpoint()}?${qs}`);
  }, [clear]);

  /** Exchange a Cognito refresh token for a fresh id/access token pair. */
  const refresh = useCallback(async (): Promise<string | null> => {
    const rt = localStorage.getItem(LS_REFRESH);
    if (!rt) return null;
    try {
      const toks = await postToken({
        grant_type:    'refresh_token',
        client_id:     authConfig.clientId,
        refresh_token: rt,
      });
      // Cognito omits refresh_token on refresh — preserve the existing one.
      applyTokens({ id_token: toks.id_token, access_token: toks.access_token });
      return toks.id_token;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[auth] refresh failed', e);
      return null;
    }
  }, [applyTokens]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    const t = idTokenState || localStorage.getItem(LS_ID_TOKEN);
    if (!t) return null;
    if (isExpired(t, 60)) return await refresh();
    return t;
  }, [idTokenState, refresh]);

  // Hydration + callback handling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Handle OAuth callback if present.
      const url    = new URL(window.location.href);
      const code   = url.searchParams.get('code');
      const state  = url.searchParams.get('state');
      const err    = url.searchParams.get('error');
      const inCbk  = url.pathname === '/callback';

      if (inCbk && (code || err)) {
        // StrictMode double-invoke guard: skip if we've already tried this code.
        if (code && consumedAuthCodes.has(code)) {
          if (!cancelled) setReady(true);
          return;
        }
        if (code) consumedAuthCodes.add(code);
        try {
          if (err) throw new Error(`cognito error: ${err} ${url.searchParams.get('error_description') || ''}`);
          const expectedState = sessionStorage.getItem(SS_STATE);
          const verifier      = sessionStorage.getItem(SS_VERIFIER);
          if (!code || !verifier || !state || state !== expectedState) {
            throw new Error('PKCE state mismatch');
          }
          const toks = await postToken({
            grant_type:    'authorization_code',
            client_id:     authConfig.clientId,
            code,
            redirect_uri:  authConfig.redirectUri(),
            code_verifier: verifier,
          });
          applyTokens(toks);
          sessionStorage.removeItem(SS_VERIFIER);
          sessionStorage.removeItem(SS_STATE);
          const back = sessionStorage.getItem(SS_RETURN_PATH) || '/jobs';
          sessionStorage.removeItem(SS_RETURN_PATH);
          // React Router navigate — swaps the active route so the <Callback>
          // spinner unmounts. `replace: true` keeps /callback out of history.
          if (!cancelled) setReady(true);
          navigate(back, { replace: true });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[auth] callback failed', e);
          if (!cancelled) setReady(true);
          navigate('/', { replace: true });
        }
        return;
      }

      // 2. Hydrate from stored id_token.
      const stored = localStorage.getItem(LS_ID_TOKEN);
      if (stored && !isExpired(stored)) {
        setIdTokenS(stored);
        setUser(decodeJwt(stored));
      } else if (stored && localStorage.getItem(LS_REFRESH)) {
        await refresh();
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [applyTokens, refresh, navigate]);

  const displayName = useCallback((): string => {
    return (user?.name as string) || (user?.email as string) || 'Observer';
  }, [user]);

  const isApproved = useMemo(() => {
    const groups = (user?.['cognito:groups'] as string[] | undefined) ?? [];
    return groups.some((g) => APPROVED_GROUPS.includes(g));
  }, [user]);

  const value = useMemo<AuthCtx>(() => ({
    ready,
    isAuthenticated: !!user,
    isApproved,
    user,
    signIn,
    signOut,
    getIdToken,
    forceRefresh: refresh,
    displayName,
  }), [ready, user, isApproved, signIn, signOut, getIdToken, refresh, displayName]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
